import { type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { cn } from '@/utils/cn';

/**
 * 移动优先 H5 外壳（Issue #198）。
 *
 * 设计：
 *   - 单列布局，宽度限制 480px，自动居中以适配大屏调试。
 *   - 顶部 PageHeader 由各页面渲染，AppShell 只负责底部导航与安全区域。
 *   - 底部 5 项主导航（首页 / 进度 / 资料 / 报告 / 账单），始终常驻。
 */

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  match: (path: string) => boolean;
}

const navItems: NavItem[] = [
  {
    to: '/',
    label: '首页',
    match: (p) => p === '/',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-5 w-5">
        <path d="M3 10l9-7 9 7v10a2 2 0 0 1-2 2h-4v-6h-6v6H5a2 2 0 0 1-2-2V10z" />
      </svg>
    ),
  },
  {
    to: '/progress',
    label: '进度',
    match: (p) => p.startsWith('/progress'),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-5 w-5">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    ),
  },
  {
    to: '/documents',
    label: '资料',
    match: (p) => p.startsWith('/documents'),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-5 w-5">
        <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
        <path d="M14 3v6h6" />
      </svg>
    ),
  },
  {
    to: '/reports',
    label: '报告',
    match: (p) => p.startsWith('/reports'),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-5 w-5">
        <path d="M4 4h16v16H4z" />
        <path d="M8 12h8M8 16h5M8 8h8" />
      </svg>
    ),
  },
  {
    to: '/finance',
    label: '账单',
    match: (p) => p.startsWith('/finance'),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-5 w-5">
        <path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
        <path d="M3 10h18" />
      </svg>
    ),
  },
];

export function AppShell({ children }: { children: ReactNode }) {
  const [path] = useLocation();
  return (
    <div className="mx-auto flex min-h-screen max-w-[480px] flex-col bg-background">
      <main className="flex-1 px-4 pt-4 pb-28 safe-top">{children}</main>
      <nav
        aria-label="主导航"
        className="fixed inset-x-0 bottom-0 z-30 mx-auto flex max-w-[480px] items-center justify-around border-t border-border bg-card/95 px-2 py-2 safe-bottom backdrop-blur"
      >
        {navItems.map((item) => {
          const active = item.match(path);
          return (
            <Link
              key={item.to}
              href={item.to}
              className={cn(
                'flex min-w-[56px] flex-col items-center gap-1 rounded-md px-2 py-1 text-xs',
                active ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  trailing,
}: {
  title: string;
  description?: string;
  trailing?: ReactNode;
}) {
  return (
    <header className="mb-4 flex items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold leading-tight">{title}</h1>
        {description ? (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {trailing}
    </header>
  );
}

export function Card({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        'rounded-lg border border-border bg-card p-4 shadow-sm',
        className,
      )}
    >
      {children}
    </section>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-card p-6 text-center">
      <p className="text-sm font-medium">{title}</p>
      {description ? (
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      ) : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

export function ErrorBanner({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div
      role="alert"
      className="mb-3 flex items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
    >
      <span>{message}</span>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="text-xs font-medium underline-offset-2 hover:underline"
        >
          重试
        </button>
      ) : null}
    </div>
  );
}

export function StageBadge({
  label,
  tone,
}: {
  label: string;
  tone: 'neutral' | 'progress' | 'warn' | 'good';
}) {
  const toneClass = {
    neutral: 'bg-muted text-muted-foreground',
    progress: 'bg-primary/10 text-primary',
    good: 'bg-emerald-100 text-emerald-700',
    warn: 'bg-amber-100 text-amber-700',
  }[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        toneClass,
      )}
    >
      {label}
    </span>
  );
}

export function PrimaryButton({
  children,
  type = 'button',
  disabled,
  onClick,
  full,
}: {
  children: ReactNode;
  type?: 'button' | 'submit';
  disabled?: boolean;
  onClick?: () => void;
  full?: boolean;
}) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60',
        full && 'w-full',
      )}
    >
      {children}
    </button>
  );
}
